/* eslint-disable camelcase */
import * as Sentry from '@sentry/browser';
import chunk from 'lodash/chunk.js';
import cloneDeep from 'lodash/cloneDeep.js';
import find from 'lodash/find.js';
import flattenDeep from 'lodash/flattenDeep.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import isNull from 'lodash/isNull.js';
import merge from 'lodash/merge.js';

import QuizStatus from '@/components/quiz-attempt/quiz-status/QuizStatus.vue';
import { handleResponseError } from '@/Helpers.js';
import User from '@/models/User.js';
import { trackAutomatedSurveyEnded } from '@/plugins/amplitude.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';
import { ModalService } from '@/services/ModalService.js';
import Item from '@/store/quill/Item.js';
import QuizAttemptHelper from '@/store/utilities/QuizAttemptHelper.js';
import CancelSource from '@/utilities/CancelSource.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';
import RetryRequest from '@/utilities/RetryRequest.js';

export const MUTATION_SET = 'SET';

export default merge(Item(), {
    autoImport: false,

    state() {
        return {
            current_question: null,
            id: null,
            in_transition: false,
            marked: [],
            must_answer_in_order: false,
            calculator_enabled: true,
            previous_question: null,
            prevent_navigation: false,
            questions: [],
            question_count: null,
            question_group_id: null,
            questions_per_page: null,
            quiz_activity: null,
            survey_activity: null,
            automated_survey_assignment: null,
            quiz_attempt: null,
            quiz_attempts: [],
            quiz_ended: null,
            quiz_overview: false,
            quiz_review: null,
            quiz_in_review: false,
            random_question_order: false,
            meta_data: {},
            time_remaining_local: null,
            time_remaining_server: null,
            title: null,
            type: null,
            modal: null,
            is_preview: false,
            is_anonymous: false,
            refresh_on_navigation: false,
            disable_answer_selection: false,
        };
    },

    getters: {
        allQuestions: (state, getters) => flattenDeep(getters.get('questions')),
    },

    actions: {
        async assignQuestionProperties({ dispatch, state, commit }, { questions }) {
            questions.forEach(item => {
                if (item.marked) commit('ADD_MARKED', { question: item });

                item.question.display_order = item.order;

                // if Show Possible Answers List is false, answers object does not exist

                if (!isEmpty(item.question.answers)) {
                    item.question.answers.forEach((answer, index) => {
                        answer.display_order = index;

                        answer.selected = false;

                        answer.loading = false;

                        if (item.type === 'free_form') answer.answer_body = null;
                    });
                }
            });

            // eslint-disable-next-line no-param-reassign
            questions = chunk(questions, state.questions_per_page);

            // eslint-disable-next-line no-shadow
            questions.forEach((item, index) => item.forEach(item => (item.question_group_id = index)));

            return dispatch('parseSelectedAnswers', { questions });
        },

        // eslint-disable-next-line consistent-return,complexity
        async endQuizAttempt({ state, commit, dispatch, rootGetters }, { isAutomatedSurvey }) {
            try {
                const typeKey = String(state.type).includes('sur') ? 'survey' : 'quiz';
                const activityIdKey = `${typeKey}_activity_id`;

                commit('SET', { target: 'in_transition', value: true });

                const api = `${rootGetters['QuizAttempt/api']('quiz_attempt')}/${state.quiz_attempt.id}`;

                const endTime = moment.utc();

                const payload = {
                    end_time: endTime.format('YYYY-MM-DD HH:mm:ss'),
                    user: state.quiz_attempt.user_id,
                };

                if (!isAutomatedSurvey) {
                    payload[activityIdKey] = state.quiz_attempt[activityIdKey];
                }

                const quizAttempt = await axios.put(api, payload).then(resp => resp.data.data);

                commit('EDIT', {
                    target: state.quiz_attempt,
                    field: 'status',
                    value: quizAttempt.status,
                });

                if (!quizAttempt.status && !state.quiz_activity.force_start) {
                    commit('User/SET', { field: 'quiz_id', value: null }, { root: true });
                    commit('User/SET', { field: 'quiz_active_id', value: null }, { root: true });
                    commit('User/SET', { field: 'quiz_activity', value: null }, { root: true });
                }

                commit('EDIT', {
                    target: state.quiz_attempt,
                    field: 'end_time',
                    value: endTime,
                });

                commit('SET', { target: 'quiz_ended', value: true });

                // Track survey end for Amplitude Targeted Replay Capture
                if (isAutomatedSurvey && state.automated_survey_assignment) {
                    const startTime = state.quiz_attempt?.start_time ? moment.utc(state.quiz_attempt.start_time) : null;
                    const durationSeconds = startTime ? endTime.diff(startTime, 'seconds') : null;

                    await trackAutomatedSurveyEnded({
                        assignmentId: state.automated_survey_assignment.id,
                        surveyId: state.automated_survey_assignment.survey_id,
                        isEvaluation: Boolean(state.automated_survey_assignment.course_id),
                        outcome: 'completed',
                        durationSeconds: durationSeconds ?? null,
                    });
                }

                if (!isNull(state.quiz_activity.time_limit)) {
                    commit('SET', { target: 'time_remaining_local', value: 0 });

                    commit('SET', { target: 'time_remaining_server', value: 0 });
                }

                if (state.quiz_activity.review_allowed && !state.quiz_activity.force_start) {
                    return dispatch('reviewQuizAttempt').then(() => {
                        commit('SET', { target: 'in_transition', value: false });
                    });
                }

                if (
                    state.quiz_activity.force_start &&
                    (quizAttempt?.status === 'ready-to-start' || quizAttempt?.status.includes('waiting-room'))
                ) {
                    dispatch('holdStatus').then(() => commit('SET', { target: 'in_transition', value: false }));
                }

                if (
                    !quizAttempt.status &&
                    !state.quiz_activity.force_start &&
                    isNull(state.quiz_activity.time_limit) &&
                    !isAutomatedSurvey
                ) {
                    const type = state.quiz_attempt.quiz_activity ? 'quiz' : 'survey';

                    const activity = state.quiz_attempt[`${type}_activity`];

                    const courseId = activity.activity.course_id;
                    const activityId = activity.activity.id;

                    window.location.href = `/courses/${courseId}/activities/${activityId}`;
                }

                if (isAutomatedSurvey) {
                    window.location.href = '/';
                }
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
                commit('SET', { target: 'in_transition', value: false });
            }
        },

        async fetchQuestions({ state, dispatch }, { quiz_attempt_id }) {
            try {
                if (!state.must_answer_in_order && !isEmpty(state.questions)) {
                    return state.questions;
                }

                const questions = await dispatch(
                    'QuizAttempt/fetch',
                    {
                        id: quiz_attempt_id,
                        query: `?filter[quiz_attempt_id]=${quiz_attempt_id}`,
                        route: 'questions',
                        type: 'all',
                    },
                    { root: true }
                );

                return dispatch('assignQuestionProperties', { questions });
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
                return [];
            }
        },

        // eslint-disable-next-line consistent-return
        async fetchQuizSubmissions({ rootGetters, state }, { activityId, userId }) {
            try {
                const survey = state.type === 'survey';

                const reviewAllowed = state.quiz_activity.review_allowed;

                const enrollmentPermissions = rootGetters['ActiveEnrollment/permissions'];

                const user = User.query().withAll().find(userId);

                const canGradeQuizzes =
                    !!find(
                        enrollmentPermissions,
                        permission => permission.action === 'create' && permission.subject === "another user's grade"
                    ) || user.is_admin;

                const queryBuilder = new QueryBuilder('activity-submissions');

                queryBuilder.filter('activity_id', activityId);

                if (userId && reviewAllowed && !canGradeQuizzes) {
                    queryBuilder.filter('created_by', userId);
                }

                queryBuilder.include(['submittable', 'creator']);

                queryBuilder.raw({ per_page: 100 });

                const data = await axios.get(queryBuilder.toUrl()).then(resp => resp.data.data);

                // new query

                if (canGradeQuizzes && !survey) {
                    const quizAttemptIds = data.map(item => {
                        return item.submittable.quiz_attempt_id;
                    });

                    const response = await axios.post('/api/quiz-attempts-bulk-instructor-review', {
                        quiz_attempt_ids: quizAttemptIds,
                    });

                    // Safe access to scoring data with null checking
                    const score = get(response, 'data.data.scoring', []);

                    if (score && score.length > 0) {
                        data.forEach(item => {
                            const attemptReview = find(
                                score,
                                attempt => attempt.quiz_attempt_id === item.submittable.quiz_attempt_id
                            );

                            if (attemptReview) {
                                item.submittable.quiz_attempt = merge(item.submittable.quiz_attempt, attemptReview);
                            }

                            return item;
                        });
                    }
                }

                // end query

                return data;
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
            }
        },

        async getRemainingTime({ state, commit, rootGetters }) {
            // noinspection LocalVariableNamingConventionJS
            const { CancelToken } = axios;

            const timezone = rootGetters['InstitutionSettings/get']('timezone');

            const expiresAt = moment.utc(state.quiz_attempt.expires_at).tz(timezone);

            const timeRemainingOnLocal = expiresAt.diff(moment(), 'seconds') || null;

            if (has(state.meta_data, 'get_remaining_time')) {
                state.meta_data.get_remaining_time.cancel();
            }

            commit('EDIT', {
                target: state.meta_data,
                field: 'get_remaining_time',
                value: CancelToken.source(),
            });

            const api = rootGetters['QuizAttempt/api']('quiz_attempt');

            const data = await axios
                .get(`${api}/${state.quiz_attempt.id}/time-remaining`, {
                    errorHandle: false,
                    cancelToken: state.meta_data.get_remaining_time.token,
                })
                .then(resp => {
                    const metaData = cloneDeep(state.meta_data);

                    delete metaData.get_remaining_time;

                    metaData.cancelled_requests = 0;

                    commit('SET', { target: 'meta_data', value: metaData });

                    return resp;
                })
                .catch(error => {
                    if (axios.isCancel(error)) {
                        const hasCancelledRequests = has(state.meta_data, 'cancelled_requests');

                        const cancelled = hasCancelledRequests ? state.meta_data.cancelled_requests : 0;

                        commit('EDIT', {
                            target: state.meta_data,
                            field: 'cancelled_requests',
                            value: cancelled + 1,
                        });
                    } else {
                        Sentry.captureException(error, {
                            tags: {
                                'crash-report': false,
                            },
                        });
                        handleResponseError(error);
                    }
                });

            if (data) {
                const timeRemainingOnServer = get(data, 'data.data.time_remaining');

                commit('EDIT', {
                    target: state.meta_data,
                    field: 'duration',
                    value: get(data, 'duration'),
                });

                commit('EDIT', {
                    target: state.meta_data,
                    field: 'empty_requests',
                    value: null,
                });

                commit('SET', {
                    target: 'time_remaining_server',
                    value: timeRemainingOnServer,
                });

                commit('SET', {
                    target: 'time_remaining_local',
                    value: timeRemainingOnServer,
                });
            } else if (!data && timeRemainingOnLocal > 0) {
                const hasEmptyServerRequest = has(state.meta_data, 'empty_requests');

                const empty = hasEmptyServerRequest ? state.meta_data.empty_requests : 0;

                commit('SET', {
                    target: 'time_remaining_local',
                    value: timeRemainingOnLocal,
                });

                commit('EDIT', {
                    target: state.meta_data,
                    field: 'empty_requests',
                    value: empty + 1,
                });
            }
        },

        async hydrateQuestions({ state, dispatch, commit }, { quiz_attempt_id }) {
            try {
                const questions = await dispatch('fetchQuestions', {
                    quiz_attempt_id,
                });

                if (!state.must_answer_in_order && !isEmpty(questions)) {
                    commit('SET', { target: 'questions', value: questions });

                    return dispatch('parseCurrentQuestion', { questions });
                }

                if (isEmpty(state.questions)) {
                    let mockQuestionList = [];
                    let i = 0;

                    while (i < state.question_count) {
                        let finalized = isEmpty(questions);

                        if (!isEmpty(questions)) {
                            // eslint-disable-next-line no-loop-func
                            questions.forEach(question =>
                                question.forEach(item => {
                                    if (item.order === i) {
                                        if (item.question.finalized) {
                                            finalized = true;
                                        }
                                    }
                                })
                            );
                        }

                        mockQuestionList.push({
                            marked: false,
                            order: i,
                            question: {
                                finalized: isEmpty(questions) ? finalized : questions[0][0].order > i,
                            },
                        });

                        i++;
                    }

                    mockQuestionList = chunk(mockQuestionList, state.questions_per_page);

                    commit('SET', {
                        target: 'questions',
                        value: mockQuestionList,
                    });
                }

                commit('SET', {
                    target: 'current_question',
                    value: isEmpty(questions) ? [] : questions[0],
                });

                return commit('SET', { target: 'question_group_id', value: 0 });
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
                return undefined;
            }
        },

        async hydrateQuizAttempt({ dispatch, commit, state }, { quiz_attempt }) {
            try {
                commit('SET', { target: 'quiz_attempt', value: quiz_attempt });

                // Set quiz_activity or survey_activity from quiz_attempt
                if (quiz_attempt.quiz_activity) {
                    commit('SET', { target: 'quiz_activity', value: quiz_attempt.quiz_activity });
                }
                if (quiz_attempt.survey_activity) {
                    commit('SET', { target: 'survey_activity', value: quiz_attempt.survey_activity });
                }

                commit('SET', {
                    target: 'is_preview',
                    value: quiz_attempt.is_preview,
                });

                commit('SET', {
                    target: 'is_anonymous',
                    value: quiz_attempt.is_anonymous,
                });

                if (quiz_attempt?.status.includes('waiting-room') || quiz_attempt?.status === 'ready-to-start') {
                    dispatch('holdStatus');

                    commit('SET', { target: 'time_remaining_local', value: 0 });

                    const activity = state.quiz_activity || state.survey_activity;
                    if (activity) {
                        commit('EDIT', {
                            target: activity,
                            field: 'time_limit',
                            value: null,
                        });
                    }

                    return;
                }

                await dispatch('getRemainingTime');

                if (quiz_attempt.status.includes('review')) {
                    dispatch('endAndApplyStatus', {
                        status: quiz_attempt.status,
                        preview: quiz_attempt.is_preview,
                    });

                    return;
                }

                await dispatch('hydrateQuestions', {
                    quiz_attempt_id: quiz_attempt.id,
                });
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
            }
        },

        async markQuestion(context, { id, question, selected_answer, value }) {
            try {
                let api = context.rootGetters['QuizAttempt/api']('questions');

                api += `/${question.id}`;

                context.commit('EDIT', {
                    target: question,
                    field: 'marked',
                    value,
                });

                if (context.state.must_answer_in_order) {
                    context.state.questions.forEach(questionGroup =>
                        questionGroup.forEach(questionInGroup => {
                            if (questionInGroup.order === question.order) {
                                questionInGroup.marked = value;
                            }
                        })
                    );
                }

                if (value) {
                    context.commit('ADD_MARKED', { question });

                    if (selected_answer != null) {
                        await context.dispatch('persistAnswer', {
                            id,
                            question: question.question,
                            answers: selected_answer,
                            finalize: question.question.finalized,
                        });
                    }
                }

                if (!value) {
                    context.commit('REMOVE_MARKED', { question });
                }

                return axios.put(api, question);
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
                return undefined;
            }
        },

        async navigate({ state, commit, dispatch }, { question }) {
            commit('SET', { target: 'disable_answer_selection', value: true });

            await dispatch('finalizeCurrentAnswers');

            // If must_answer_in_order is true, we need to refetch questions
            // because the backend only sends the current question(s)
            if (state.must_answer_in_order) {
                commit('SET', {
                    target: 'previous_question',
                    value: state.current_question,
                });

                await dispatch('hydrateQuestions', {
                    quiz_attempt_id: state.quiz_attempt.id,
                });
            } else {
                // If not must_answer_in_order, all questions are already loaded
                commit('SET', {
                    target: 'current_question',
                    value: state.questions[question.question_group_id],
                });

                commit('SET', {
                    target: 'question_group_id',
                    value: question.question_group_id,
                });

                if (question.question_group_id > 0) {
                    commit('SET', {
                        target: 'previous_question',
                        value: state.questions[question.question_group_id - 1],
                    });
                }
            }

            commit('SET', { target: 'disable_answer_selection', value: false });
        },

        async finalizeCurrentAnswers({ state, dispatch }) {
            if (!state.current_question || !Array.isArray(state.current_question)) return;

            await Promise.all(
                state.current_question.map(async currentItem => {
                    const questionObj = currentItem.question;

                    if (questionObj.finalized) {
                        return;
                    }

                    // For free_form questions, check answer_body instead of selected_answer
                    if (currentItem.type === 'free_form') {
                        const answerBody = questionObj.answers?.[0]?.answer_body;

                        // If answer_body is empty, skip persisting
                        if (!answerBody || answerBody.trim() === '' || answerBody === '<p></p>') {
                            return;
                        }
                    } else {
                        const selections = questionObj.selected_answer;

                        // If selections is null or an empty array, skip persisting
                        if (selections === null || (Array.isArray(selections) && !selections.length)) {
                            return;
                        }
                    }

                    let answers = [];

                    if (currentItem.type === 'free_form') {
                        // For free-form we pass the existing answers array (may contain answer_body)
                        answers = questionObj.answers ? questionObj.answers.map(a => ({ ...a })) : [];
                    } else {
                        const selections = questionObj.selected_answer;
                        const mapSelection = selIdx =>
                            questionObj.answers.find(a => Number(a.display_order) === Number(selIdx));

                        if (Array.isArray(selections)) {
                            answers = selections.map(mapSelection).filter(Boolean);
                        } else {
                            const ans = mapSelection(selections);
                            if (ans) answers = [ans];
                        }
                    }

                    if (!answers.length) {
                        return;
                    }

                    try {
                        await dispatch('persistAnswer', {
                            id: currentItem.id,
                            answers,
                            question: questionObj,
                            finalize: true,
                        });
                    } catch (err) {
                        Sentry.captureException(err, {
                            tags: {
                                'crash-report': false,
                            },
                        });
                    }
                })
            );
        },

        async next({ commit, dispatch, state }) {
            commit('SET', { target: 'disable_answer_selection', value: true });

            await dispatch('finalizeCurrentAnswers');

            commit('SET', {
                target: 'previous_question',
                value: state.current_question,
            });

            if (state.quiz_ended) {
                await dispatch('parseCurrentQuestion', {
                    questions: state.questions,
                });

                commit('SET', { target: 'disable_answer_selection', value: false });
                return;
            }

            await dispatch('hydrateQuestions', {
                quiz_attempt_id: state.quiz_attempt.id,
            });

            commit('SET', { target: 'disable_answer_selection', value: false });
        },

        parseSelectedAnswers(context, { questions }) {
            const helper = new QuizAttemptHelper({ store: context });

            return new Promise(resolve => {
                questions.forEach(question => {
                    helper.parseSelectedAnswers(question);
                });

                resolve(questions);
            });
        },

        parseCurrentQuestion({ state, commit }, { questions, review = false }) {
            const params = new Proxy(new URLSearchParams(window.location.search), {
                get: (searchParams, prop) => searchParams.get(prop),
            });

            if (
                params.question &&
                params.question >= 0 &&
                params.question - 1 <= questions.length &&
                !state.quiz_in_review
            ) {
                const questionNumber = Number(params.question) - 1;

                const questionIndex = questions.findIndex(
                    question =>
                        // eslint-disable-next-line no-shadow
                        question.findIndex(question => Number(question.question.display_order) === questionNumber) >= 0
                );

                commit('SET', {
                    target: 'previous_question',
                    value: questionIndex === 0 ? null : questions[questionIndex - 1],
                });

                commit('SET', {
                    target: 'current_question',
                    value: questions[questionIndex],
                });

                commit('SET', { target: 'question_group_id', value: questions[questionIndex][0].question_group_id });

                return;
            }

            if (isEmpty(state.current_question)) {
                const index = questions.findIndex(
                    item => item.findIndex(question => !question.question.finalized) >= 0
                );

                // if review is true, set question group to display first question

                const question_group_id = index >= 0 && !review ? questions[index][0].question_group_id : 0;

                commit('SET', {
                    target: 'current_question',
                    value: questions[question_group_id],
                });

                commit('SET', {
                    target: 'question_group_id',
                    value: question_group_id,
                });

                return;
            }

            const current = state.current_question[0].question_group_id;

            commit('SET', {
                target: 'previous_question',
                value: state.current_question,
            });

            commit('SET', {
                target: 'current_question',
                value: questions[current + 1],
            });

            commit('SET', { target: 'question_group_id', value: current + 1 });
        },

        setAnswer({ commit }, payload) {
            commit('EDIT', payload);
        },

        async persistAnswer(context, { id, answers, question, finalize }) {
            // Set prevent_navigation to true at the start of any persist operation
            context.commit('SET', {
                target: 'prevent_navigation',
                value: true,
            });

            try {
                const cancel = CancelSource({
                    type: `QuizAttempt/${id}/persistAnswer`,
                });
                let api = context.rootGetters['QuizAttempt/api']('answers');
                const freeForm = question.type === 'free_form';
                let apiType = 'post';

                answers.forEach(answer => (answer.answer_id = answer.id));

                let payload = {
                    answers,
                    finalize,
                    quiz_attempt_question_id: id,
                    response: null,
                };

                if (freeForm && answers[0].answer_attempt_id) {
                    apiType = 'put';

                    api += `/${answers[0].answer_attempt_id}`;

                    payload = {
                        finalize,
                        answer_body: answers[0].answer_body,
                    };
                }

                const retry = new RetryRequest({
                    question,
                    message:
                        'An error occurred when trying to save your answer. Please refresh the page. If the problem persists, please seek assistance.',
                });

                const data = await retry.request({
                    retryLimit: 15,
                    action: () => {
                        return axios[apiType](`${api}`, payload, {
                            ...cancel.axiosConfig,
                            errorHandle: false,
                        }).then(resp => (freeForm ? [resp.data.data] : resp.data.data));
                    },
                });

                if (question.type === 'free_form' && apiType === 'post') {
                    context.commit('EDIT', {
                        target: question.answers[0],
                        field: 'answer_attempt_id',
                        value: data[0].id,
                    });
                }

                // Reset previously selected answers;
                question.answers.forEach(answer => {
                    context.commit('EDIT', {
                        target: answer,
                        field: 'selected',
                        value: false,
                    });
                });

                answers.forEach(answer => {
                    context.commit('EDIT', {
                        target: answer,
                        field: 'selected',
                        value: true,
                    });
                });

                context.commit('EDIT', {
                    target: question,
                    field: 'finalized',
                    value: finalize,
                });

                if (context.state.must_answer_in_order) {
                    context.state.questions.forEach(questionGroup =>
                        questionGroup.forEach(questionInGroup => {
                            if (questionInGroup.order === question.display_order) {
                                questionInGroup.question.finalized = finalize;
                            }
                        })
                    );
                }

                context.commit('EDIT', {
                    target: question,
                    field: 'finalized_at',
                    value: data[0].finalized_at,
                });

                context.commit('SET', {
                    target: 'prevent_navigation',
                    value: false,
                });
            } catch (error) {
                // Check if the error is due to request cancellation
                if (axios.isCancel(error)) {
                    // Don't log as an error since cancellation is expected behavior
                } else {
                    console.error('Error in persistAnswer:', error);
                    // Report non-cancellation errors to Sentry
                    Sentry.captureException(error, {
                        tags: {
                            'crash-report': false,
                        },
                    });
                }
            } finally {
                // Always reset prevent_navigation to false when the operation completes
                // This ensures navigation is re-enabled even if the request was cancelled
                context.commit('SET', {
                    target: 'prevent_navigation',
                    value: false,
                });
            }
        },

        async previous({ state, commit, dispatch }) {
            commit('SET', { target: 'disable_answer_selection', value: true });

            await dispatch('finalizeCurrentAnswers');

            commit('SET', {
                target: 'current_question',
                value: state.questions[state.question_group_id - 1],
            });

            commit('SET', {
                target: 'question_group_id',
                value: state.question_group_id - 1,
            });

            commit('SET', { target: 'disable_answer_selection', value: false });
        },

        async reviewQuizAttempt({ commit, dispatch, state, rootGetters }) {
            try {
                const api = `${rootGetters['QuizAttempt/api']('quiz_attempt')}/${state.quiz_attempt.id}/review`;

                const quiz_review = await axios.get(api).then(resp => resp.data.data);

                let questions = quiz_review.quiz_attempt_questions;

                commit('SET', { target: 'quiz_in_review', value: true });

                commit('SET', { target: 'quiz_overview', value: false });

                commit('SET', { target: 'current_question', value: [] });

                commit('SET', { target: 'quiz_review', value: quiz_review });

                commit('SET', { target: 'must_answer_in_order', value: false });

                questions = await dispatch('assignQuestionProperties', {
                    questions,
                });

                commit('SET', { target: 'questions', value: questions });

                await dispatch('parseCurrentQuestion', { questions, review: true });
            } catch (error) {
                Sentry.captureException(error, {
                    tags: {
                        'crash-report': false,
                    },
                });
            }
        },

        toggleAnswerLoading({ state, commit }, { answer, index, status }) {
            const { question } = state.current_question[index];
            const multipleAnswer = question.type.includes('answer');

            // Note: prevent_navigation is now managed by persistAnswer action
            // Only manage individual answer loading states here
            if (multipleAnswer) {
                answer.forEach(selection => {
                    const answerIndex = question.answers.findIndex(
                        answerInList => answerInList.display_order === selection
                    );

                    commit('EDIT', {
                        target: question.answers[answerIndex],
                        field: 'loading',
                        value: status,
                    });
                });
            } else {
                const answerIndex = question.answers.findIndex(answerInList => answerInList.display_order === answer);

                commit('EDIT', {
                    target: question.answers[answerIndex],
                    field: 'loading',
                    value: status,
                });
            }
        },

        async toggleReview({ state, commit, dispatch }) {
            commit('SET', { target: 'disable_answer_selection', value: true });

            if (!state.quiz_overview) {
                await dispatch('finalizeCurrentAnswers');
            }

            commit('SET', {
                target: 'quiz_overview',
                value: !state.quiz_overview,
            });

            commit('SET', { target: 'disable_answer_selection', value: false });
        },

        endAndApplyStatus({ commit, dispatch, state }, { status = null }) {
            commit('SET', { target: 'in_transition', value: true });

            commit('EDIT', {
                target: state.quiz_attempt,
                field: 'status',
                value: status,
            });

            if (!state.quiz_ended) {
                const endTime = moment.utc();

                commit('EDIT', {
                    target: state.quiz_attempt,
                    field: 'end_time',
                    value: endTime,
                });

                commit('SET', { target: 'quiz_ended', value: true });

                if (!isNull(state.quiz_activity.time_limit)) {
                    commit('SET', { target: 'time_remaining_local', value: 0 });

                    commit('SET', {
                        target: 'time_remaining_server',
                        value: 0,
                    });
                }
            }

            const waitingRoom =
                status && (state.quiz_attempt?.status.includes('waiting-room') || status.includes('waiting-room'));

            if (state.quiz_attempt.status === 'ready-to-start' || waitingRoom) {
                dispatch('holdStatus').then(() => commit('SET', { target: 'in_transition', value: false }));
            }

            if (state.quiz_attempt.status === 'in-review' || status === 'in-review') {
                dispatch('reviewQuizAttempt').then(() => {
                    commit('SET', { target: 'in_transition', value: false });

                    dispatch('closeModal');
                });
            }

            if (!status) {
                commit('User/SET', { field: 'quiz_id', value: null }, { root: true });
                commit('User/SET', { field: 'quiz_active_id', value: null }, { root: true });
                commit('User/SET', { field: 'quiz_activity', value: null }, { root: true });

                dispatch('closeModal');
            }
        },

        holdStatus({ commit, dispatch, state, rootGetters }) {
            if (state.modal) dispatch('closeModal');

            const modal = ModalService.open({
                fullScreen: true,
                canCancel: false,
                component: QuizStatus,
                customClass: 'has-background-white',
                props: {
                    status: state?.quiz_attempt?.status,
                },
            });

            commit('SET', { target: 'modal', value: modal });
        },

        closeModal({ commit, state, rootGetters }) {
            const userId = rootGetters['User/get']('id');
            const userModel = User.find(userId);

            const modal = state.modal || userModel.quiz_modal;

            if (modal) {
                modal.close();

                commit('SET', { target: 'modal', value: null });
                User.commit(modelState => {
                    modelState.data[userModel.id].quiz_modal = null;
                });
            }
        },

        cleanHydratedAttempt({ commit }) {
            const defaultState = {
                current_question: null,
                in_transition: false,
                quiz_in_review: false,
                quiz_overview: false,
                quiz_ended: false,
                quiz_attempt: null,
                questions: [],
                marked: [],
            };

            Object.keys(defaultState).forEach(key => {
                commit('SET', { target: key, value: defaultState[key] });
            });
        },
    },

    mutations: {
        SET(state, { target, value }) {
            state[target] = value;
        },

        EDIT(state, { target, field, value }) {
            target[field] = value;
        },

        ADD_MARKED(state, { question }) {
            state.marked.push(cloneDeep(question));
        },

        REMOVE_MARKED(state, { question }) {
            const index = state.marked.findIndex(item => question.id === item.id);

            state.marked.splice(index, 1);
        },
    },
});
